import { useEffect, useState } from 'react'

// 伺服器告訴前台的設定（#1724）。是否顯示開發元素由伺服器的環境決定，
// 前台不自己猜：同一份建置在各環境的差別只來自這一支端點。

export interface ClientModelOption {
  id: string
  displayName: string
}

export interface StepBounds {
  min: number
  max: number
  default: number
}

// 非 dev 時伺服器只回 showDevTools，其餘欄位一律可能缺漏
export interface ClientConfig {
  showDevTools: boolean
  models?: ClientModelOption[]
  defaultModel?: string
  maxSteps?: StepBounds
  // 思考預算上下界與預設（#1739）
  thinkingBudget?: StepBounds
}

// 🔴 讀取失敗或格式不對一律當成「不顯示開發元素」：降級只少了開發工具，聊天照常
export const FALLBACK_CLIENT_CONFIG: ClientConfig = { showDevTools: false }

const isModelOption = (value: unknown): value is ClientModelOption => {
  const v = value as Partial<ClientModelOption> | null
  return typeof v?.id === 'string' && typeof v.displayName === 'string'
}

const isStepBounds = (value: unknown): value is StepBounds => {
  const v = value as Partial<StepBounds> | null
  return [v?.min, v?.max, v?.default].every((n) => typeof n === 'number')
}

// 各欄位各自檢查，壞掉或缺漏的那一欄就不帶，不連累 showDevTools
export const parseClientConfig = (body: unknown): ClientConfig => {
  const raw = (body ?? {}) as Record<string, unknown>
  const config: ClientConfig = { showDevTools: raw.showDevTools === true }
  if (Array.isArray(raw.models) && raw.models.every(isModelOption)) config.models = raw.models
  if (typeof raw.defaultModel === 'string') config.defaultModel = raw.defaultModel
  const experiment = raw.experiment as { maxSteps?: unknown; thinkingBudget?: unknown } | undefined
  if (isStepBounds(experiment?.maxSteps)) config.maxSteps = experiment.maxSteps
  if (isStepBounds(experiment?.thinkingBudget)) config.thinkingBudget = experiment.thinkingBudget
  return config
}

export const fetchClientConfig = async (): Promise<ClientConfig> => {
  try {
    const res = await fetch('/api/ai/client-config')
    if (!res.ok) return FALLBACK_CLIENT_CONFIG
    return parseClientConfig(await res.json())
  } catch {
    return FALLBACK_CLIENT_CONFIG
  }
}

// 讀回來之前先用降級值，開發元素只會「晚出現」，不會先出現再消失
export const useClientConfig = (): ClientConfig => {
  const [config, setConfig] = useState<ClientConfig>(FALLBACK_CLIENT_CONFIG)
  useEffect(() => {
    let alive = true
    void fetchClientConfig().then((loaded) => alive && setConfig(loaded))
    return () => {
      alive = false
    }
  }, [])
  return config
}
